import React from "react";
import {
    AreaChart,
    Area,
    CartesianGrid,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer
} from "recharts";
import styled from "styled-components";

export const BaseChartStyled = styled.div`
  width: 100%;
`;

export const TooltipshiChartStyled = styled.div`
  background: rgba(20, 21, 26, 0.95);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  padding: 10px 14px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.5);
  z-index: 100;
  
  p.time {
    font-size: 10px;
    color: rgba(255, 255, 255, 0.45);
    font-weight: 600;
    margin: 0 0 6px 0;
    letter-spacing: 0.5px;
    text-transform: uppercase;
  }

  .tooltip-item {
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 4px 0;
  }

  .indicator-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    display: inline-block;
  }

  p.label {
    font-size: 12px;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.9);
    margin: 0;
    font-family: Consolas, monospace;
  }
`;

export const getChartTheme = (key) => {
    const normalizedKey = (key || '').toLowerCase();
    
    const themes = {
        cpu: { stroke: '#6366f1', fillStart: '#6366f1', fillStop: 'rgba(99, 102, 241, 0)' },
        load_cpu: { stroke: '#6366f1', fillStart: '#6366f1', fillStop: 'rgba(99, 102, 241, 0)' },
        ram: { stroke: '#06b6d4', fillStart: '#06b6d4', fillStop: 'rgba(6, 182, 212, 0)' },
        memory: { stroke: '#06b6d4', fillStart: '#06b6d4', fillStop: 'rgba(6, 182, 212, 0)' },
        net: { stroke: '#f97316', fillStart: '#f97316', fillStop: 'rgba(249, 115, 22, 0)' },
        net2: { stroke: '#22c55e', fillStart: '#22c55e', fillStop: 'rgba(34, 197, 94, 0)' },
        network: { stroke: '#f97316', fillStart: '#f97316', fillStop: 'rgba(249, 115, 22, 0)' },
        disk: { stroke: '#f43f5e', fillStart: '#f43f5e', fillStop: 'rgba(244, 63, 94, 0)' },
        disk_io: { stroke: '#ed8936', fillStart: '#ed8936', fillStop: 'rgba(237, 137, 54, 0)' },
        connections: { stroke: '#3b82f6', fillStart: '#3b82f6', fillStop: 'rgba(59, 130, 246, 0)' },
        processes: { stroke: '#10b981', fillStart: '#10b981', fillStop: 'rgba(16, 185, 129, 0)' },
        file_handles: { stroke: '#d946ef', fillStart: '#d946ef', fillStop: 'rgba(217, 70, 239, 0)' },
        sessions: { stroke: '#eab308', fillStart: '#eab308', fillStop: 'rgba(234, 179, 8, 0)' }
    };

    return themes[normalizedKey] || { stroke: '#6366f1', fillStart: '#6366f1', fillStop: 'rgba(99, 102, 241, 0)' };
};

const totalTheme = {
    stroke: 'rgba(255, 255, 255, 0.25)',
    fillStart: 'rgba(255, 255, 255, 0.05)',
    fillStop: 'rgba(255, 255, 255, 0)'
};

const BaseLineChart = ({ data }) => {
    if (!data || !data.key) {
        return null;
    }
    const dataKey = data.key;
    const theme = getChartTheme(dataKey);
    const secondTheme = getChartTheme(data.key2);

    const gradientId = `gradient-${dataKey}`;
    const totalGradientId = `total-gradient-${dataKey}`;
    const secondGradientId = `gradient-${data.key2}`;

    return (
        <BaseChartStyled>
            <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={data.data} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                    <defs>
                        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor={theme.fillStart} stopOpacity={0.25}/>
                            <stop offset="95%" stopColor={theme.fillStop} stopOpacity={0.0}/>
                        </linearGradient>
                        <linearGradient id={totalGradientId} x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor={totalTheme.fillStart} stopOpacity={0.06}/>
                            <stop offset="95%" stopColor={totalTheme.fillStop} stopOpacity={0.0}/>
                        </linearGradient>
                        { data.key2 && (
                            <linearGradient id={secondGradientId} x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor={secondTheme.fillStart} stopOpacity={0.25}/>
                                <stop offset="95%" stopColor={secondTheme.fillStop} stopOpacity={0.0}/>
                            </linearGradient>
                        ) }
                    </defs>
                    
                    {/* Render Total Capacity Limit line if present in data */}
                    <Area 
                        type="monotone" 
                        dataKey="total" 
                        stroke={totalTheme.stroke} 
                        strokeWidth={1.5} 
                        strokeDasharray="3 3"
                        fill={`url(#${totalGradientId})`} 
                        unit={data.ext} 
                        activeDot={{ r: 4 }}
                        name="Total"
                    />
                    
                    {/* Render Utilization line */}
                    <Area 
                        type="monotone" 
                        dataKey={dataKey} 
                        stroke={theme.stroke} 
                        strokeWidth={2} 
                        fill={`url(#${gradientId})`} 
                        unit={data.ext} 
                        activeDot={{ r: 5, strokeWidth: 0 }}
                        name={data.label || dataKey}
                    />

                    { data.key2 && (
                        <Area
                            type="monotone"
                            dataKey={data.key2}
                            stroke={secondTheme.stroke}
                            strokeWidth={2}
                            fill={`url(#${secondGradientId})`}
                            unit={data.ext}
                            activeDot={{ r: 5, strokeWidth: 0 }}
                            name={data.label2 || data.key2}
                        />
                    ) }

                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.03)" horizontal={true} vertical={false}/>
                    
                    <XAxis 
                        dataKey="name" 
                        stroke="rgba(255, 255, 255, 0.3)" 
                        fontSize={9} 
                        tickLine={false}
                        axisLine={false}
                        dy={8}
                    />
                    
                    <YAxis 
                        stroke="rgba(255, 255, 255, 0.3)" 
                        fontSize={9} 
                        tickLine={false}
                        axisLine={false}
                        unit={data.ext}
                        dx={-4}
                    />
                    
                    <Tooltip content={<CustomTooltip themeColor={theme.stroke} />} />
                </AreaChart>
            </ResponsiveContainer>
        </BaseChartStyled>
    );
};

export default BaseLineChart;

function CustomTooltip(props) {
    const { payload, label, active, themeColor } = props;

    if (active && payload && payload.length > 0) {
        return (
            <TooltipshiChartStyled className="custom-tooltip">
                <p className="time">{ label }</p>
                { payload.map((relod, idx) => {
                    const displayName = relod.name === 'rsp' 
                        ? 'Latency' 
                        : (relod.name.charAt(0).toUpperCase() + relod.name.slice(1).replace('_', ' '));
                    
                    const isTotal = relod.name.toLowerCase() === 'total';
                    const dotColor = isTotal
                        ? 'rgba(255, 255, 255, 0.4)'
                        : (relod.stroke || themeColor);

                    return (
                        <div key={ idx + "dddw200" } className="tooltip-item">
                            <span className="indicator-dot" style={{ backgroundColor: dotColor }} />
                            <p className="label">{ `${ displayName }: ${ relod.value } ${ relod.unit || '' }` }</p>
                        </div>
                    );
                }) }
            </TooltipshiChartStyled>
        );
    }

    return null;
}
